import React from "react";
import { Row, Col, Tag, Typography, Empty, Space, Divider } from "antd";
import {
  FileProtectOutlined,
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  SendOutlined,
} from "@ant-design/icons";
import THEME from "../theme";
import { formatMoney, formatDate } from "../utils";
import StyledCard from "../components/StyledCard";

const { Text } = Typography;

const getStageMeta = (latest) => {
  if (latest.cancelDate || latest.active === 0)
    return { color: "default", label: "ยกเลิก" };
  if (latest.receiveDate) return { color: "green", label: "รับงานแล้ว" };
  return { color: "orange", label: "รอรับงาน" };
};

// โซนระยะทาง: 1=(0-150กม.) / 2=(151-300กม.) / 3=(301-500กม.) / 4=(501-800กม.) / E=(800กม.ขึ้นไป)
const getDistanceZone = (distance) => {
  if (distance == null) return null;
  if (distance <= 150) return 1;
  if (distance <= 300) return 2;
  if (distance <= 500) return 3;
  if (distance <= 800) return 4;
  return "E";
};

const getDistance = (distance) => {
  if (distance == null) return null;
  if (distance <= 1) return 150;
  if (distance <= 2) return 300;
  if (distance <= 3) return 500;
  if (distance <= 4) return 800;
  return 1000;
};

const debtStats = (latest) => [
  {
    label: "ยอดหนี้ตามคำพิพากษา",
    value: formatMoney(latest.snapTotalJudge),
    color: THEME.primary,
  },
  // {
  //   label: "เงินต้นคงเหลือ",
  //   value: formatMoney(latest.snapTonBalance),
  //   color: THEME.primary,
  // },
  {
    label: "ยอดค้างชำระ",
    value: formatMoney(latest.snapExpAmt),
    color: THEME.warning,
  },
  {
    label: "เบี้ยปรับคงเหลือ",
    value: formatMoney(latest.snapResBeeypup),
    color: Number(latest.snapResBeeypup) > 0 ? THEME.danger : THEME.success,
  },
];

const EnforcementTab = ({ judgeData }) => {
  const latest = judgeData?.enforcement?.latest;

  if (!latest) return <Empty description="ไม่มีข้อมูล" />;

  const stage = getStageMeta(latest);
  const isCancelled = !!(latest.cancelDate || latest.active === 0);

  return (
    <Row style={{ marginBottom: 16 }}>
      <Col span={24}>
        <StyledCard
          title="ข้อมูลส่งบังคับคดี"
          icon={
            <FileProtectOutlined
              style={{ color: THEME.primary, fontSize: 18 }}
            />
          }
          extra={
            <Space size={4}>
              <Tag color={stage.color}>{latest.stageText || stage.label}</Tag>
              {latest.round != null && <Tag>รอบที่ {latest.round}</Tag>}
            </Space>
          }
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Text
                strong
                style={{ display: "block", marginBottom: 10, color: "#888" }}
              >
                การมอบหมายงาน
              </Text>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <div>
                  <UserOutlined style={{ color: "#888", marginRight: 6 }} />
                  <Text type="secondary">มอบหมายให้ </Text>
                  <Text strong>{latest.receiveByName || "-"}</Text>
                  {latest.assignByName && (
                    <Text type="secondary"> (โดย {latest.assignByName})</Text>
                  )}
                </div>
                <div>
                  <CalendarOutlined style={{ color: "#888", marginRight: 6 }} />
                  <Text type="secondary">วันที่มอบหมาย </Text>
                  <Text>{formatDate(latest.assignDate)}</Text>
                </div>
                <div>
                  <CalendarOutlined style={{ color: "#888", marginRight: 6 }} />
                  <Text type="secondary">วันที่รับงาน </Text>
                  <Text>{formatDate(latest.receiveDate)}</Text>
                </div>
                {latest.distance != null && (
                  <div>
                    <EnvironmentOutlined
                      style={{ color: "#888", marginRight: 6 }}
                    />
                    <Text type="secondary">ระยะทาง ≈ </Text>
                    <Text>
                      {getDistance(latest.distance)} กม. (โซน{" "}
                      {getDistanceZone(latest.distance)})
                    </Text>
                  </div>
                )}
                {latest.sendByName && (
                  <div>
                    <SendOutlined style={{ color: "#888", marginRight: 6 }} />
                    <Text type="secondary">ส่งงานโดย </Text>
                    <Text>{latest.sendByName}</Text>
                    <Text type="secondary">
                      {" "}
                      ({formatDate(latest.sendDate)})
                    </Text>
                  </div>
                )}
                {isCancelled && (
                  <div style={{ color: THEME.danger }}>
                    ยกเลิก {formatDate(latest.cancelDate)}
                    {latest.cancelByName ? ` โดย ${latest.cancelByName}` : ""}
                    {latest.cancelRemark ? ` (${latest.cancelRemark})` : ""}
                  </div>
                )}
                {latest.remark && (
                  <div>
                    <Text type="secondary">หมายเหตุ </Text>
                    <Text>{latest.remark}</Text>
                  </div>
                )}
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Text
                strong
                style={{ display: "block", marginBottom: 10, color: "#888" }}
              >
                สรุปยอดหนี้
              </Text>
              <Row gutter={[12, 12]}>
                {debtStats(latest).map(({ label, value, color }) => (
                  <Col span={12} key={label}>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        padding: "8px 12px",
                      }}
                    >
                      <Text
                        type="secondary"
                        style={{ fontSize: 12, display: "block" }}
                      >
                        {label}
                      </Text>
                      <Text strong style={{ color, fontSize: 16 }}>
                        {value}
                      </Text>
                    </div>
                  </Col>
                ))}
              </Row>
              <Divider style={{ margin: "12px 0" }} />
              <Space size={16} wrap>
                <Text type="secondary">
                  ค้างงวด: <Text strong>{latest.snapHldno ?? "-"}</Text> งวด
                </Text>
                <Text type="secondary">
                  ขาดการติดต่อ: <Text strong>{latest.snapLostDay ?? "-"}</Text>{" "}
                  วัน
                </Text>
                {/* <Tag color={latest.snapFoundAsset ? "green" : "default"}>
                  {latest.snapFoundAsset ? "พบทรัพย์" : "ยังไม่พบทรัพย์"}
                </Tag> */}
              </Space>
            </Col>
          </Row>
        </StyledCard>
      </Col>
    </Row>
  );
};

export default EnforcementTab;
